import axios, { CancelTokenSource } from 'axios'
import React, { useRef, useState } from 'react'
import { Icon, IconType } from '../Icon'

interface Props {
  fileUrl: string
  fileName: string
  mimeType?: string
}

const CircularProgress = ({
  progress,
  isHovering,
}: {
  progress: number
  isHovering: boolean
}) => {
  const circumference = 2 * Math.PI * 10
  const strokeDashoffset = circumference - (progress / 100) * circumference

  return (
    <div className='relative inline-flex items-center justify-center w-5 h-5'>
      <svg className='absolute transform -rotate-90 w-7 h-7 -left-1 -top-1'>
        <circle
          className='text-gray-200'
          strokeWidth='3'
          stroke='currentColor'
          fill='transparent'
          r='10'
          cx='14'
          cy='14'
        />
        <circle
          className='text-indigo-700 animate-pulse'
          strokeWidth='3'
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap='round'
          stroke='currentColor'
          fill='transparent'
          r='10'
          cx='14'
          cy='14'
        />
      </svg>
      {isHovering ? (
        <Icon type={IconType.x} className='h-3.5 w-3.5 text-red-500 z-10' />
      ) : (
        <span className='absolute text-[11px] font-bold text-indigo-800 '>
          {Math.round(progress)}
        </span>
      )}
    </div>
  )
}

const createTemporaryLinkAndClick = ({
  responseData,
  fileName,
  mimeType,
}: {
  responseData: Blob
  fileName: string
  mimeType?: string
}) => {
  const url = mimeType
    ? window.URL.createObjectURL(new Blob([responseData], { type: mimeType }))
    : window.URL.createObjectURL(new Blob([responseData]))
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', `${fileName}`)

  document.body.appendChild(link)

  link.click()
  link.parentNode?.removeChild(link)

  window.URL.revokeObjectURL(url)
}

export const DownloadFileButton: React.FC<Props> = ({
  fileUrl,
  fileName,
  mimeType,
}) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [progress, setProgress] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const cancelTokenRef = useRef<CancelTokenSource | null>(null)

  const cancelDownload = () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Download cancelled by user')
      setIsDownloading(false)
      setProgress(0)
    }
  }

  const downloadContent = async () => {
    try {
      setIsDownloading(true)
      setProgress(0)

      cancelTokenRef.current = axios.CancelToken.source()

      const response = await axios.get(fileUrl, {
        responseType: 'blob', // handle binary data
        cancelToken: cancelTokenRef.current.token,
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const percentage = total ? (progressEvent.loaded / total) * 100 : 0
          setProgress(percentage)
        },
      })

      createTemporaryLinkAndClick({
        responseData: response.data,
        fileName: fileName,
        mimeType: mimeType,
      })
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Download Error:', error)
      }
    } finally {
      setIsDownloading(false)
      setProgress(0)
      cancelTokenRef.current = null
    }
  }

  return (
    <button
      onClick={isDownloading ? cancelDownload : downloadContent}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={`flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-gray-100 p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200 ${!isDownloading ? 'hover:text-gray-600 hover:bg-gray-200' : 'cursor-pointer hover:bg-red-50'}`}
    >
      {isDownloading ? (
        <CircularProgress isHovering={isHovering} progress={progress} />
      ) : (
        <Icon type={IconType.download} className='h-5 w-5 text-primary' />
      )}
    </button>
  )
}
